<template>
  <div class="col-12 relative grey-bg full-height flex flex-column">
    <template v-if="routeMatchesComponent() || !route.meta.breadcrumb">
      <!-- TODO Improves -->
      <CategoryHeader :title="route.meta.title" :icon="route.meta.icon" />
    </template>
    <div v-if="loading" class="relative full-height flex items-center justify-center pb5">
      <div class="center">
        <div class="list-loader-blue"></div>
      </div>
    </div>

    <div v-else class="p3">
      <div v-for="(participant,index) in participantsAllowedProduct" :key="participant.uuid" class="flex flex-wrap m0 p0" :class="{'mt2': index != 0}">
        <Collapsable class="col-12">
          <template #header>
            <strong>Participant: </strong>{{ participant.fullname }}
          </template>
          <template #content>
            <div v-for="(allowedProduct,productIndex) in participant.computedAllowedProduct" v-if="participant.computedAllowedProduct && participant.computedAllowedProduct.length" :key="`AllowedProduct_${productIndex}`">
              <Collapsable collapsable-header="collapsable-header">
                <template #header>
                  <div class="h4n">
                    {{ allowedProduct.product.name }}
                  </div>
                </template>
                <template #content>
                  <div v-for="(offer,offerIndex) in allowedProduct.product.dynamo_offers" :key="offerIndex" class="flex flex-wrap m0 py1 px2" :class="{'mt2': offerIndex != 0}" style="border-bottom: solid 1px black;">
                    <div class="col-4 md-col-3">
                      {{ offer.name }}
                    </div>
                    <div class="col-4 md-col-3 center">
                      <div class="col-12">
                        À partir de
                      </div>
                      <div class="col-12 center">
                        {{ `${offer.detail.lowest_price_label}${offer.detail.recurring?'/mois':''}` }}
                      </div>
                    </div>
                    <div class="col-4 md-col-3">
                      <div class="col-12 center">
                        Date validité:
                      </div>
                      <div v-if="offer.detail?.end_timestamp" class="col-12 center">
                        {{ offer.detail.end_timestamp }}
                      </div>
                      <div v-else class="col-12 center">
                        --
                      </div>
                    </div>
                    <div class="col-12 md-col-3 sm-flex center items-center flex-grow justify-end">
                      <a :href="`${generateFunnelUrl(offer)}&user=${participant.uuid}`" :class="['large-text white-color black-bg py-small px3 col-12', { 'disabled' : offer.isDisabled }]">
                        Acheter
                      </a>
                    </div>
                  </div>
                </template>
              </Collapsable>
            </div>

            <!-- renew order -->
            <div
              v-for="(order,orderIndex) in participant.renewableOrder"
              v-if="participant.renewableOrder && participant.renewableOrder.length"
              :key="`renewalOrder_${orderIndex}`"
              class="flex flex-wrap m0 p0"
            >
              <Collapsable collapsable-header="collapsable-header">
                <template #header>
                  <div class="h4n">
                    {{ order.name }}
                  </div>
                </template>
                <template #content>
                  <div class="flex flex-wrap m0 py1 px2" :class="{'mt2': orderIndex != 0}" style="border-bottom: solid 1px black;">
                    <div class="col-4 md-col-3">
                      {{ order.name }}
                    </div>
                    <div class="col-4 md-col-3 center">
                      <div class="col-12">
                        À partir de
                      </div>
                      <div class="col-12 center">
                        {{ `${order.price}${order.recurring?'/mois':''}` }}
                      </div>
                    </div>
                    <div class="col-4 md-col-3">
                      <div class="col-12 center">
                        Date validité:
                      </div>
                      <div v-if="order.end_timestamp" class="col-12 center">
                        {{ order.end_timestamp }}
                      </div>
                      <div v-else class="col-12 center">
                        --
                      </div>
                    </div>
                    <div class="col-12 md-col-3 sm-flex center items-center flex-grow justify-end">
                      <a :class="['large-text white-color black-bg py-small px3 col-12 pointer', { 'disabled' : order.isDisabled }]" @click="handlePeriodRenewal(order)">
                        Renouveler l'achat
                      </a>
                    </div>
                  </div>
                </template>
              </Collapsable>
            </div>

            <div v-if="showDefaultMessage(participant)" class="h4n p2">
              Vous ne disposez pas d’offres actuellement. N’hésitez pas à revenir consulter votre espace personnel afin de profiter de nouvelles offres.
            </div>
          </template>
        </Collapsable>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance, onBeforeMount, ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { isAfter, isWithinInterval, startOfDay, endOfDay } from 'date-fns';

import { routeIsCurrentComponent, generateFunnelUrl, isObjectEmpty } from '@/mixins/utils';
import { getOrderProductAndPeriod, orderRenewal } from '@/mixins/requests';

import CategoryHeader from '@/components/common/CategoryHeader';
import Collapsable from '@/components/common/Collapsable';

export default {
  components: { CategoryHeader, Collapsable },
  setup () {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const loading = ref(false);
    const participants = computed(() => store.state.contacts);

    const workspace = computed(() => store.state.siteInfo.workspace);
    const orders = computed(() => (store.state.user.orders || []).filter(o => o.site_workspace === store.state.siteInfo.workspace).map(order => ({ ...order, items: order.order.order_items })));

    const ordersPeriod = ref({});

    const participantRenewableOrder = participantUuid => {
      const renewableItem = [];

      Object.keys(ordersPeriod.value).map(key => {
        const orderPeriod = ordersPeriod.value[key].period;
        const orderProduct = ordersPeriod.value[key].product;
        if (!orderPeriod || (orderPeriod && !orderPeriod.renewedWith)) {
          return false;
        }

        if (!orderProduct) {
          return false;
        }

        if (orderProduct.sales_status && orderProduct.sales_status !== 'AUTOMATIC') {
          return false;
        }

        if (isAfter(new Date(orderPeriod.renewedWith.salesStartDate), new Date())) {
          return false;
        }

        if (isAfter(new Date(), new Date(orderPeriod.renewedWith.validityEndDate))) {
          return false;
        }

        const renewalOffer = orderProduct.dynamo_offers.find(offer => offer.detail.prices.some(p => {
          const doesSalesStart = isWithinInterval(new Date(), { start: startOfDay(new Date(p.period.salesStartDate)), end: endOfDay(new Date(p.period.salesEndDate)) });

          return p.period.uuid === orderPeriod.renewedWith.uuid && doesSalesStart;
        }));

        if (!renewalOffer) {
          return false;
        }

        if (!renewalOffer.detail || (renewalOffer.detail && (renewalOffer.detail.reservation || renewalOffer.detail.inscription) && !renewalOffer.detail.haveSessions)) {
          return false;
        }

        const haveOrderWIthRenewedPeriod = orders.value.some(order => (order.order.order_items || []).some(order_item => {
          if (order_item.period) {
            return order_item.period.uuid === orderPeriod.renewedWith.uuid && order_item.customer === participantUuid;
          }
          return false;
        }));

        if (haveOrderWIthRenewedPeriod) {
          return false;
        }

        let lowestPrice = Number.MAX_SAFE_INTEGER;

        orderProduct.dynamo_offers.map(offer => {
          const lowest = offer.detail.lowest_price_promo || offer.detail.lowest_price;
          if (lowestPrice > lowest) {
            lowestPrice = lowest;
          }
        });

        renewableItem.push({
          name: ordersPeriod.value[key].product.name,
          price: `${parseFloat(lowestPrice / 100).toFixed(2)} €`,
          recurring: orderProduct.dynamo_offers.some(offer => offer.detail.recurring),
          order: ordersPeriod.value[key].order,
          orderPeriod,
          orderProduct,
          renewalOffer
        });
      });

      return renewableItem;
    };

    const participantsAllowedProduct = computed(() => participants.value.map(participant => {
      const allowedProducts = participant.allowed_products.filter(ap => {
        if (ap && ap.product && ap.product.workspace === workspace.value) {
          return ap;
        }
      });

      participant.fullname = `${participant.firstname} ${participant.lastname}`;
      participant.computedAllowedProduct = allowedProducts.map(ap => ({ ...ap, customer_uuid: participant.uuid }));

      // participant.computedAllowedProduct = participant.computedAllowedProduct.filter(ap => !(orders.value || []).some(order => order.isPaid && (order.items || []).some(i => i.customer.uuid === ap.customer_uuid && i.product_uuid === ap.product_uuid)));

      if (!isObjectEmpty(ordersPeriod.value)) {
        participant.renewableOrder = participantRenewableOrder(participant.uuid).filter(order => order.order.items.some(item => item.customer.uuid === participant.uuid));
      }

      return participant;
    }));

    const routeMatchesComponent = () => routeIsCurrentComponent(route.name, getCurrentInstance());

    const productLowestPrice = offers => {
      let lowest_price = Number.MAX_SAFE_INTEGER;
      offers.map(offer => {
        if (offer.detail && offer.detail.lowest_price && offer.detail.lowest_price < lowest_price) {
          lowest_price = offer.detail.lowest_price;
        }
      });

      return lowest_price !== Number.MAX_SAFE_INTEGER && lowest_price > 0 ? `${(lowest_price / 100).toFixed(2)} €` : 'Offert';
    };

    const handlePeriodRenewal = async ({ order, orderPeriod, orderProduct, renewalOffer }) => {
      loading.value = true;
      const response = await orderRenewal(order.uuid, orderPeriod.renewedWith, orderProduct);
      const { siteInfo, impersonateData } = store.state;
      if (response && response.success) {
        let url = `${response.funnelUrl}`;

        switch (response.code) {
          case 'REDIRECT_TO_TDA': {
            url = `${url}/recapitulatif?workspace=${siteInfo.workspace}`;
            break;
          }
          case 'REDIRECT_TO_TDA_ENTRY': {
            url = `${url}?workspace=${siteInfo.workspace}&product=${orderProduct.uuid}&offer=${renewalOffer.uuid}&order=${order.uuid}`;
          }
          default:
        }
        if (impersonateData) {
          url = `${url}&impersonateData=${encodeURI(impersonateData)}`;
        }

        loading.value = false;

        window.open(
          `${url}`,
          '_blank'
        );
      }
    };

    const showDefaultMessage = participant => {
      if (participant.renewableOrder && participant.renewableOrder.length) {
        return false;
      }

      if (participant.computedAllowedProduct && participant.computedAllowedProduct.length) {
        return false;
      }

      return true;
    };

    onBeforeMount(async () => {
      ordersPeriod.value = {};

      loading.value = true;

      const promises = orders.value.map(async order => {
        if (order.items.some(item => item.period)) {
          // make request to get product/period
          // all participant will have same product and same period
          const response = await getOrderProductAndPeriod(order.items[0].product_uuid, order.items[0].period.uuid);

          if (response) {
            if (!ordersPeriod.value.hasOwnProperty(order.uuid)) {
              ordersPeriod.value[order.uuid] = {};
            }

            ordersPeriod.value[order.uuid].product = response.product;
            ordersPeriod.value[order.uuid].period = response.period;
            ordersPeriod.value[order.uuid].order = order;
          }
        }
      });

      await Promise.all(promises);

      loading.value = false;
    });

    return { showDefaultMessage, handlePeriodRenewal, generateFunnelUrl, participantsAllowedProduct, route, routeMatchesComponent, participants, loading, productLowestPrice };
  }
};

</script>

<style>

.collapsable-header {
  background-color: #d6d6d6;
}

</style>
